/**
 * maeMfe — Maximum Adverse / Favorable Excursion calculator.
 *
 * Pure function. Given a closed (or in-progress) trade and the OHLCV bars
 * covering its lifetime, computes:
 *
 *   - maePoints: how far price moved AGAINST the position from entry
 *                (positive number; 0 if never went against you)
 *   - mfePoints: how far price moved IN FAVOR of the position from entry
 *                (positive number; 0 if never went your way)
 *   - maeR / mfeR: same in R-multiples (null if no stopLoss to derive risk)
 *   - maeDollars / mfeDollars: same in dollars (null if symbol not in spec)
 *   - maeAt / mfeAt: Unix-sec timestamps of the bars where each occurred
 *   - barsConsidered: how many bars fell inside the trade window
 *
 * Why three units (points, R, dollars):
 *   - Points: universal — works for any instrument, no spec lookup needed.
 *   - R-multiples: the most meaningful unit for "you left X on the table"
 *     coaching observations — but requires knowing the trade's risk.
 *   - Dollars: matches what broker MAE/MFE imports already produce, so the
 *     existing `useAIAnalysis` confluence tracker (which reads `trade.mae`/
 *     `trade.mfe` as parsed numbers) can consume the result without
 *     downstream changes.
 *
 * Returns null when there's no usable data — caller should treat that as
 * "skip MAE/MFE for this trade", not as "MAE was zero".
 */

import { getSpec } from '../../config/futuresSpecs.js';
import { getExecutionInstantMs } from '../timeService.js';
import { aggregateBars } from '../barData/aggregateBars.js';
import { computeTrendContextScore } from './trendContextScore.js';
import { computeEntryDiscipline } from './entryDiscipline.js';
import { detectSetupPatterns } from './setupPatterns.js';
import { computeEmas, emaSnapshotAtTime, DEFAULT_EMA_PERIODS } from './emas.js';
import { computeVWAP, vwapAtTime } from './vwap.js';
import { computeAvwapPack, avwapSnapshotAtTime } from './avwap.js';
import { deriveAvwapConfluenceZonesForTrade } from './avwapConfluenceZones.js';
import { detectFourHourBodySR } from './fourHourBodySR.js';
import { buildVolumeProfile } from './volumeProfile.js';
import { computeKeyLevels } from './keyLevels.js';
import { computeMultiTfState } from './multiTfState.js';
import { computeInitialBalance } from './initialBalance.js';

/**
 * Resolve a trade's entry and exit timestamps to Unix seconds.
 *
 * @param {Object} trade
 * @returns {{ entryTs: number, exitTs: number } | null}
 *          null if the trade has no parseable entry time.
 */
const resolveTradeWindow = (trade) => {
  if (!trade?.date) return null;
  const entryMs = getExecutionInstantMs(trade.date, trade.entryTime);
  if (!entryMs) return null;

  let exitMs;
  if (trade.exitTime) {
    exitMs = getExecutionInstantMs(trade.date, trade.exitTime);
    // Defensive: an exit before entry (clock skew, manual entry typo) →
    // ignore the exit and treat as open trade
    if (!exitMs || exitMs < entryMs) exitMs = null;
  }

  return {
    entryTs: Math.floor(entryMs / 1000),
    exitTs: exitMs ? Math.floor(exitMs / 1000) : null,
  };
};

/**
 * Infer bar period (seconds) from successive bar timestamps. Median of
 * consecutive deltas — robust against missing bars or session gaps. Used
 * by the walk to extend its inclusion window so a bar that *contains* the
 * trade's exit instant is included even if its stamp falls just past it.
 */
const inferBarPeriodSec = (bars) => {
  if (!Array.isArray(bars) || bars.length < 2) return null;
  const deltas = [];
  for (let i = 1; i < bars.length; i++) {
    const d = Number(bars[i]?.time) - Number(bars[i - 1]?.time);
    if (Number.isFinite(d) && d > 0) deltas.push(d);
  }
  if (deltas.length === 0) return null;
  deltas.sort((a, b) => a - b);
  const mid = Math.floor(deltas.length / 2);
  return deltas.length % 2 === 0
    ? Math.round((deltas[mid - 1] + deltas[mid]) / 2)
    : deltas[mid];
};

/**
 * Given a trade with possibly-multiple exits (the journal supports partial
 * exits), return a single "average exit price" weighted by quantity. Used
 * only for log-style debugging — MAE/MFE itself doesn't need exit price.
 */
const _avgExitPrice = (trade) => {
  if (Array.isArray(trade?.exits) && trade.exits.length > 0) {
    let totalQ = 0, totalQP = 0;
    for (const e of trade.exits) {
      const q = Number(e.quantity) || 0;
      const p = Number(e.price);
      if (q > 0 && Number.isFinite(p)) { totalQ += q; totalQP += q * p; }
    }
    if (totalQ > 0) return totalQP / totalQ;
  }
  const fallback = Number(trade?.exit);
  return Number.isFinite(fallback) ? fallback : null;
};

/**
 * Compute MAE/MFE for a trade against its bars.
 *
 * @param {Object} trade           Must have: date, entryTime, entry (price), direction.
 *                                 May have:  exitTime, stopLoss, symbol, quantity, exits[].
 * @param {Array}  bars            [{ time: unixSec, high, low, close, ... }, ...] sorted asc.
 * @param {Object} [opts]
 * @param {number[]} [opts.emaPeriods]  EMA periods to snapshot at entry (default: standard 9/21/50/200).
 *                                      Pass [] to skip EMA computation.
 * @param {Object} [opts.htfBars]       Optional map of higher-timeframe label → bars[].
 *                                      Example: { '15m': [...], '1h': [...], '4h': [...] }.
 *                                      Each TF's EMAs are snapshotted at the entry time and
 *                                      stored under `htfEmas[tf]`. Bars must be sorted asc.
 *                                      Trader workflow: 4H/1H/15M structure → 5M/15M entry,
 *                                      and the Coach uses these snapshots to ask "did this
 *                                      entry align with the bigger trend or fight it?"
 * @returns {Object|null}  Result object, or null if not computable.
 */
export const computeMaeMfe = (trade, bars, opts = {}) => {
  if (!Array.isArray(bars) || bars.length === 0) return null;

  const window = resolveTradeWindow(trade);
  if (!window) return null;
  const { entryTs, exitTs } = window;

  const entryPrice = Number(trade?.entry);
  if (!Number.isFinite(entryPrice) || entryPrice <= 0) return null;

  const isLong = trade?.direction !== 'short';

  // Walk bars whose time-range OVERLAPS the trade window — not whose stamp
  // happens to fall inside it.
  //
  // Each bar's `time` field is a representative stamp for the bar's full
  // period. With the project convention of open-time stamps (enforced at
  // the source layer; see `NinjaTraderBarSource._normalizeNTTimestamps`),
  // a bar covers real-time `[stamp, stamp + barPeriodSec)`.
  //
  // The trade window is `[entryTs, exitTs]` (or `[entryTs, ∞)` for an open
  // trade). A bar overlaps the window when:
  //
  //     stamp + barPeriodSec  >  entryTs    (bar's end is after trade entry)
  //     stamp                 <= exitTs     (bar's start is at or before exit)
  //
  // The earlier `t < entryTs continue / t > exitTs break` gate excluded the
  // bar containing the entry instant (its stamp could be entryTs - some
  // fraction) and similarly excluded the bar containing the exit instant.
  // For trades whose entry and exit are inside a single bar at the requested
  // TF, that meant 0 of the trade's actual price action was walked — the
  // root cause of the "Sharp entry on a stop-out" defect.
  // Bar period is needed to know each bar's full range. When there's
  // only one bar (or all bars share the same stamp), we can't infer it —
  // fall back to the strict stamp-based gate. That's the same semantics
  // as before this fix and the right answer for single-bar sanity checks
  // where the bar's range is implicit in the test setup.
  const barPeriodSec = inferBarPeriodSec(bars);
  let lowestLow = Infinity;
  let lowestLowAt = null;
  let highestHigh = -Infinity;
  let highestHighAt = null;
  let barsConsidered = 0;

  for (const b of bars) {
    const t = Number(b?.time);
    if (!Number.isFinite(t)) continue;
    // Entry-side gate: with a known bar period, skip only when the bar's
    // entire range is before entry. Without a period, fall back to the
    // strict stamp comparison.
    if (barPeriodSec) {
      if (t + barPeriodSec <= entryTs) continue;
    } else {
      if (t < entryTs) continue;
    }
    // Exit-side gate: stop when the bar's start is past trade exit.
    // (NT close-time bars are normalized to open-time at the source layer
    // before reaching here, so this gate is correct for all sources.)
    if (exitTs != null && t > exitTs) break;

    const hi = Number(b.high);
    const lo = Number(b.low);
    if (!Number.isFinite(hi) || !Number.isFinite(lo)) continue;

    barsConsidered++;
    if (lo < lowestLow)   { lowestLow   = lo; lowestLowAt   = t; }
    if (hi > highestHigh) { highestHigh = hi; highestHighAt = t; }
  }

  if (barsConsidered === 0) return null;

  // Sanity guard: if the trade's recorded exit price falls outside the
  // bar-derived [lowestLow, highestHigh] range, the bars don't fully cover
  // what the trade actually did. Could be a JournifiBars data gap, a
  // mid-bar broker fill the bars don't reflect, or a manual exit whose
  // price doesn't match what the chart shows. Better to return null than
  // emit confidently-wrong MAE/MFE that'll mislead the Coach. The 0.01
  // tolerance absorbs floating-point noise on broker prices.
  const exitPx = Number(trade?.exit);
  if (Number.isFinite(exitPx) && exitPx > 0 && trade?.exitTime) {
    if (exitPx < lowestLow - 0.01 || exitPx > highestHigh + 0.01) {
      return null;
    }
  }

  // Points adverse / favorable. Clamp at zero — a trade whose price never
  // went against the position should report MAE = 0, not negative.
  const maePoints = isLong
    ? Math.max(0, entryPrice - lowestLow)
    : Math.max(0, highestHigh - entryPrice);
  const mfePoints = isLong
    ? Math.max(0, highestHigh - entryPrice)
    : Math.max(0, entryPrice - lowestLow);

  const maeAt = isLong ? lowestLowAt : highestHighAt;
  const mfeAt = isLong ? highestHighAt : lowestLowAt;

  // R-multiples — require an explicit stopLoss with non-zero risk distance.
  // We allow `useStopLossPoints` mode by deriving from `stopLossPoints` too.
  let riskPoints = null;
  const sl = Number(trade?.stopLoss);
  if (Number.isFinite(sl) && sl > 0) {
    const r = Math.abs(entryPrice - sl);
    if (r > 0) riskPoints = r;
  } else if (trade?.useStopLossPoints && Number.isFinite(Number(trade?.stopLossPoints))) {
    const r = Math.abs(Number(trade.stopLossPoints));
    if (r > 0) riskPoints = r;
  }
  const maeR = riskPoints ? maePoints / riskPoints : null;
  const mfeR = riskPoints ? mfePoints / riskPoints : null;

  // Dollars — require futures spec + a quantity. Quantity defaults to 1 if
  // the trade has none (consistent with the rest of the journal's defaults).
  let maeDollars = null;
  let mfeDollars = null;
  const spec = trade?.symbol ? getSpec(String(trade.symbol).toUpperCase()) : null;
  const qty = Math.max(1, Math.floor(Number(trade?.quantity) || 1));
  if (spec && Number.isFinite(spec.pointValue)) {
    // MAE is the LOSS magnitude, so report as positive (matches how broker
    // exports do it — "MAE: $42" not "-$42"). Same sign convention for MFE.
    maeDollars = maePoints * spec.pointValue * qty;
    mfeDollars = mfePoints * spec.pointValue * qty;
  }

  // ── EMA snapshot at entry ─────────────────────────────────────────────
  // Compute the requested EMAs once and look up their values at the entry
  // bar's timestamp. Stored as flat object the Coach can read without
  // recomputing: emasAtEntry: { 9: 21450.5, 21: 21449.0, 50: 21442.0, 200: 21430.0 }
  // Periods that haven't warmed up by entry time return null for that key.
  let emasAtEntry = null;
  const emaPeriods = Array.isArray(opts.emaPeriods) ? opts.emaPeriods : DEFAULT_EMA_PERIODS;
  if (emaPeriods.length > 0) {
    try {
      const allEmaSeries = computeEmas(bars, emaPeriods);
      emasAtEntry = emaSnapshotAtTime(allEmaSeries, entryTs);
    } catch {
      // EMA failure shouldn't poison MAE/MFE — leave snapshot null.
    }
  }

  // ── HTF EMA snapshots ─────────────────────────────────────────────────
  // Per-timeframe EMA dictionaries at entry time. Keyed by TF label so the
  // analyzer surface stays compact and forward-compatible (adding 30m or
  // 2h later doesn't require a schema migration).
  //
  // Stored as { '15m': { 5:..., 9:..., 21:..., 50:..., 200:... }, '1h': {...}, '4h': {...} }
  // Each TF entry is the same shape as `emasAtEntry`. Periods that haven't
  // warmed up by entry time return null for that key. A TF with empty bars
  // is silently dropped — analyzers reading `htfEmas` already handle missing
  // keys.
  let htfEmas = null;
  if (opts.htfBars && typeof opts.htfBars === 'object' && emaPeriods.length > 0) {
    htfEmas = {};
    for (const [tf, htfBarsArr] of Object.entries(opts.htfBars)) {
      if (!Array.isArray(htfBarsArr) || htfBarsArr.length === 0) continue;
      try {
        const series = computeEmas(htfBarsArr, emaPeriods);
        const snap = emaSnapshotAtTime(series, entryTs);
        if (snap) htfEmas[tf] = snap;
      } catch {
        // One bad TF shouldn't poison the rest — skip it.
      }
    }
    if (Object.keys(htfEmas).length === 0) htfEmas = null;
  }

  // ── VWAP snapshot at entry ────────────────────────────────────────────
  // Single value: the session-anchored VWAP at the entry bar. The Coach
  // uses this for "you went long below VWAP" / "VWAP reclaim" observations.
  // null when bars don't carry volume or the entry precedes the session.
  let vwapAtEntry = null;
  try {
    const vwapSeries = computeVWAP(bars);
    vwapAtEntry = vwapAtTime(vwapSeries, entryTs);
  } catch {
    // VWAP failure shouldn't poison the rest of the result.
  }

  // ── AVWAP pack at entry (Wave D, v7.5.1) ──────────────────────────────
  // Map of anchor → AVWAP value at the entry bar. Anchors covered:
  // prior-day-high/low/close, session-high/low. Used by the Coach for
  // institutional-level interaction observations ("you entered at AVWAP
  // from prior day high — a classic supply zone"). The pack is computed
  // off the same 1m/5m bars passed in; anchors that can't be resolved
  // (e.g., prior-day-high when bars only cover today) are silently omitted.
  // Null when no anchors resolve at all.
  let avwapAtEntry = null;
  try {
    const pack = computeAvwapPack(bars);
    if (pack && Object.keys(pack).length > 0) {
      const snap = avwapSnapshotAtTime(pack, entryTs);
      if (snap && Object.keys(snap).length > 0) avwapAtEntry = snap;
    }
  } catch {
    // AVWAP failure shouldn't poison the rest of the result.
  }

  // ── Key levels (Wave E.2, v7.5.1) ─────────────────────────────────────
  // Static horizontal reference levels — Golden Crayon (PDH+PDL/2), prior-
  // day OHLC, today's open + intraday extremes. The Golden Crayon is the
  // user's primary inflection level (the 2026-05-18 missed short was a
  // Golden Crayon rejection). Stored as a snapshot computed once; downstream
  // chip detection in tradeFlags checks entry-price proximity.
  let keyLevels = null;
  try {
    keyLevels = computeKeyLevels({ bars, targetDay: trade?.date });
  } catch {
    // Key-levels failure shouldn't poison the rest.
  }

  // ── AVWAP confluence zones (Top-Down B.5, v7.5.15) ────────────────────
  // Multi-AVWAP convergence detection per Brian Shannon's framework — when
  // multiple anchored VWAPs land within a tight price band, that band is an
  // institutional reference cluster with higher reaction probability than
  // any single anchor. Closes the v7.4.3 audit §3.2 open item.
  //
  // Stored as a top-level barAnalysis field (sibling of avwapAtEntry,
  // which it's derived from) rather than nested inside keyLevels — keeps
  // the backfill's LATEST_FIELDS detection straightforward (top-level
  // field check) and matches the architectural pattern of sibling AVWAP
  // data. The design doc's §5.1 originally nested under keyLevels; this
  // flatter shape was easier to detect for re-backfill, so we adopted it.
  //
  // Default tolerance is 1.0 point ≈ 4 ticks on 0.25-tick instruments
  // (MNQ/NQ/ES/MES) — accurate for the user's primary symbols. YM
  // (1.0 tick) gets a looser convergence band as a result; per-instrument
  // tuning is a v2 refinement.
  let avwapConfluenceZones = null;
  try {
    if (avwapAtEntry) {
      const zones = deriveAvwapConfluenceZonesForTrade(avwapAtEntry);
      if (Array.isArray(zones) && zones.length > 0) {
        avwapConfluenceZones = zones;
      }
    }
  } catch {
    // Zone derivation failure shouldn't poison the rest of barAnalysis.
  }

  // ── 4H body-close S/R (Top-Down B.3, v7.5.15) ─────────────────────────
  // Cluster detection on 4H bar close prices — when ≥3 4H bars close
  // within a 1.0-point band, that's recurrent S/R the user's NinjaTrader
  // "4H S/R (Body)" indicator already surfaces on his chart. Coach now
  // captures the same level. Single-link clustering identical to
  // avwapConfluenceZones (intentional — familiar code path).
  //
  // Source: opts.htfBars['4h'] when provided. Empty array when no 4H
  // bars are available (single-day trades, fresh installs, etc.).
  let fourHourBodySR = null;
  try {
    const bars4h = opts?.htfBars?.['4h'];
    if (Array.isArray(bars4h) && bars4h.length > 0) {
      const levels = detectFourHourBodySR(bars4h);
      if (Array.isArray(levels) && levels.length > 0) {
        fourHourBodySR = levels;
      }
    }
  } catch {
    // 4H S/R failure shouldn't poison the rest.
  }

  // ── Volume Profile (Top-Down B.4, v7.5.15) ────────────────────────────
  // POC / VAH / VAL / HVN / LVN derived from the trading day's bars via
  // the uniform-volume-distribution proxy (per Top-Down §7.1). Tickwise
  // resolution defaults to 0.25 (MNQ/NQ/ES/MES); per-instrument tickSize
  // is a v2 refinement.
  //
  // **Limitation:** bar-level VP is an approximation — intra-bar volume
  // distribution is unknown. Industry-standard caveat. Upgradeable to
  // true tick prints when the Polygon.io secondary bar source ships.
  //
  // Computed over the trade's full bar window (passed-in `bars`). For
  // strict RTH-only VP, caller can slice bars to 9:30-16:00 ET first.
  let volumeProfile = null;
  try {
    if (Array.isArray(bars) && bars.length > 0) {
      const profile = buildVolumeProfile(bars);
      if (profile && Number.isFinite(profile.poc)) {
        volumeProfile = profile;
      }
    }
  } catch {
    // VP failure shouldn't poison the rest of barAnalysis.
  }

  // ── Trend-context score (Wave A, v7.4.4) ──────────────────────────────
  // Single 0–5 universal trend-alignment score. The MAE/MFE walk uses 1m
  // bars (for precision); trendContextScore wants 5m for the entry-bar
  // EMA slope + ADX(14) computation. aggregateBars is a no-op when the
  // input is already at the requested cadence, so passing `bars` here
  // works whether they're 1m or 5m. HTF slopes use opts.htfBars directly
  // (typically 15m / 1h / 4h).
  //
  // Stored on barAnalysis.trendContextScore. Every downstream analyzer
  // can filter on it (e.g., "your 5M 21EMA setup wins 64% at score≥3
  // and 22% at score≤1"). Returns null if not computable; downstream
  // analyzers must tolerate that.
  let trendContextScore = null;
  let entryDiscipline = null;
  let setupPatterns = null;
  try {
    const bars5m = aggregateBars(bars, 5);
    trendContextScore = computeTrendContextScore({
      entryBars: bars5m,
      htfBars: opts.htfBars,
      entryTs,
      direction: trade?.direction,
    });

    // ── Entry discipline (Wave B.2) ─────────────────────────────────
    // Same 5m bars feed the stalker/chaser classification. Runs cheaply
    // alongside trendContextScore — no extra fetch.
    entryDiscipline = computeEntryDiscipline({
      bars: bars5m,
      entryTs,
      entryPrice,
      direction: trade?.direction,
    });

    // ── Setup patterns (Wave C) ─────────────────────────────────────
    // Run all 7 mechanical pattern detectors (Wolf Stack, 3-Bar Play,
    // Holy Grail, Turtle Soup, Uno Reverse, H2/L2, Signal Bar Quality).
    // Stored as an array; trade can fire multiple. Empty array when
    // none match. Holy Grail receives the already-computed ADX from
    // trendContextScore to avoid recomputing.
    setupPatterns = detectSetupPatterns({
      bars: bars5m,
      entryTs,
      entryPrice,
      direction: trade?.direction,
      adxAtEntry: trendContextScore?.adxAtEntry,
    });
  } catch {
    // Score / discipline / patterns failures shouldn't poison the rest of the analysis.
  }

  // ── Multi-TF state vector (Top-Down Phase A, v7.5.15) ─────────────────
  // Composes per-TF state primitives (stage / EMA stack + slopes / always-in
  // / trend integrity / RSI / volume regime) across all 5 timeframes per
  // docs/research/Coach_TopDown_Analysis.md §2.1. Entry-TF bars (the `bars`
  // arg) are aggregated to 5m to match the user's entry timeframe convention;
  // HTF bars come from opts.htfBars. Daily bars are optional — when missing,
  // the daily slot is a fully-null state (graceful degradation).
  //
  // Stored on barAnalysis.multiTf as the snapshot at entryTs. Used by the
  // upcoming Top-Down Coach analyzers (Phase C) to compute bias/zone/trigger
  // coherence per trade.
  let multiTf = null;
  try {
    const bars5m = aggregateBars(bars, 5);
    multiTf = computeMultiTfState({
      five:     bars5m,
      fifteen:  opts.htfBars?.['15m'],
      oneHour:  opts.htfBars?.['1h'],
      fourHour: opts.htfBars?.['4h'],
      daily:    opts.htfBars?.['1d'] || opts.htfBars?.['daily'],
    }, { asOfTime: entryTs });
  } catch {
    // Multi-TF failure shouldn't poison the rest of the result.
  }

  // ── Initial Balance (Top-Down Phase B, v7.5.15) ───────────────────────
  // 9:30–10:30 ET opening range — IB high/low/mid + breakout/reentry
  // classification at the entry bar. Port of the user's
  // OpenRangeOptimized_WithArrows.cs indicator. Stored on
  // barAnalysis.initialBalance. Trade-card chips + Top-Down zone
  // observations (Phase C) read from this field.
  let initialBalance = null;
  try {
    initialBalance = computeInitialBalance({
      bars,
      targetDay: trade?.date,
      entryTs,
    });
  } catch {
    // IB failure shouldn't poison the rest of the result.
  }

  return {
    maePoints,
    mfePoints,
    maeR,
    mfeR,
    maeDollars,
    mfeDollars,
    maeAt,
    mfeAt,
    barsConsidered,
    emasAtEntry,
    vwapAtEntry,
    avwapAtEntry,
    avwapConfluenceZones,
    fourHourBodySR,
    volumeProfile,
    keyLevels,
    htfEmas,
    trendContextScore,
    entryDiscipline,
    setupPatterns,
    multiTf,
    initialBalance,
    computedAt: new Date().toISOString(),
    source: 'bars',
  };
};
